import { useCallback } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../context/AppContext.jsx';
import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';
import { parseFormData } from '../services/WaypointService.js';

export function useWaypointEditor() {
  const {
    parser,
    currentWaypoints,
    setCurrentWaypoints,
    currentFeatures,
  } = useFile();
  const { selectedWaypointIndex, selectedIsViaPoint } = useSelection();
  const { visualizer } = useVisualizer();
  const { showStatus } = useUI();

  const updateWaypoint = useCallback(async (formData) => {
    if (selectedWaypointIndex === null) return;

    try {
      // Use service to parse form data (converts mm/degrees to meters/radians)
      const { position: newPosition, rotation: newRotation, error } = parseFormData(formData);
      
      if (error) {
        throw new Error(error);
      }

      if (selectedIsViaPoint) {
        // Update via-point
        const waypoint = currentWaypoints[selectedWaypointIndex];
        if (waypoint.viaPoint) {
          parser.updateViaPointPosition(selectedWaypointIndex, newPosition);
          parser.updateViaPointRotation(selectedWaypointIndex, newRotation);

          // Update local data (store feature-local coords)
          waypoint.viaPoint.position = newPosition;
          waypoint.viaPoint.rotation = newRotation;

          // Re-visualize
          if (visualizer) {
            const waypointsForVis = convertWaypointsToWorldCoords(currentWaypoints, currentFeatures);
            visualizer.visualizeWaypoints(waypointsForVis);
          }

          showStatus('Via-point updated successfully', 'success');
        }
      } else {
        // Update regular waypoint with feature-local coordinates
        parser.updateWaypointPosition(selectedWaypointIndex, newPosition);
        parser.updateWaypointRotation(selectedWaypointIndex, newRotation);

        // Update local data (store feature-local coords)
        const updatedWaypoints = [...currentWaypoints];
        updatedWaypoints[selectedWaypointIndex] = {
          ...updatedWaypoints[selectedWaypointIndex],
          position: newPosition,
          rotation: newRotation,
        };
        setCurrentWaypoints(updatedWaypoints);

        // Re-visualize all waypoints to ensure visualizer's internal waypoint data is updated
        if (visualizer) {
          const waypointsForVis = convertWaypointsToWorldCoords(updatedWaypoints, currentFeatures);
          visualizer.visualizeWaypoints(waypointsForVis, false); // false = don't center camera
        }

        showStatus('Waypoint updated successfully', 'success');
      }
    } catch (error) {
      console.error('Error updating waypoint:', error);
      showStatus(`Error: ${error.message}`, 'error');
    }
  }, [parser, visualizer, currentWaypoints, setCurrentWaypoints, selectedWaypointIndex, selectedIsViaPoint, currentFeatures, showStatus]);

  const changeConfig = useCallback(async (configIndex, applyIKConfigFn, cachedSolutions) => {
    if (selectedWaypointIndex === null) return;
    
    try {
      // Apply the selected config using the IK solver's applyIKConfig function
      // Pass cached solutions to avoid recalculation
      const updatedWaypoints = await applyIKConfigFn(selectedWaypointIndex, configIndex, cachedSolutions);
      
      if (updatedWaypoints) {
        const updatedWaypoint = updatedWaypoints[selectedWaypointIndex];
        
        // Update the robot model to show the new configuration
        if (visualizer && visualizer.ur10 && updatedWaypoint.jointPositions) {
          const jointAngles = updatedWaypoint.jointPositions.map(rad => rad * 180 / Math.PI);
          visualizer.ur10.setJointAngles(jointAngles);
        }
        
        // Re-visualize all waypoints to show the new configuration markers
        if (visualizer) {
          const waypointsForVis = convertWaypointsToWorldCoords(updatedWaypoints, currentFeatures);
          visualizer.visualizeWaypoints(waypointsForVis, false);
        }
      }
    } catch (error) {
      console.error('Error changing config:', error);
      showStatus(`Error: ${error.message}`, 'error');
    }
  }, [selectedWaypointIndex, visualizer, currentFeatures, showStatus]);

  return { updateWaypoint, changeConfig };
}
